import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraOverlayDrawer, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link, useLocation } from 'react-router-dom';
import { useTenantId } from '../../pages/Delivery/_shared/hooks/useTenantId';
import { useCompanyProfiles } from '../../pages/Delivery/Programs/referenceDataHooks';
import { useCompanyVehicleHoldings } from '../../pages/Delivery/Registries/companyEvidenceHooks';
import { contractVehicleHooks } from '../../pages/Delivery/Registries/hooks';
import { registryRecordHref } from './sharedRecordLinks';
const T = Testids.sharedRecord;
/**
 * Quick inspection (C7A-05). Read-only: it identifies the shared object and its
 * permitted connections and links to the full record. It owns no selection or
 * draft state, so closing changes nothing in the originating work.
 */ export function SharedRecordPreview({ kind, id, open, onClose }) {
    const tenantId = useTenantId();
    const location = useLocation();
    const origin = location.pathname + location.search + location.hash;
    const label = kind === 'company' ? 'Company' : 'Vehicle';
    const companies = useCompanyProfiles();
    const vehicles = contractVehicleHooks.useList({
        companyId: tenantId
    }, {
        enabled: kind === 'vehicle'
    });
    const holdings = useCompanyVehicleHoldings(tenantId, kind === 'company' ? id : '');
    const company = kind === 'company' ? companies.data?.find((c)=>c.id === id) : undefined;
    const vehicle = kind === 'vehicle' ? vehicles.data?.items.find((v)=>v.id === id) : undefined;
    const record = company ?? vehicle;
    const loading = kind === 'company' ? companies.isLoading : vehicles.isLoading;
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: open,
        onOpenChange: (next)=>!next && onClose(),
        title: record?.name || label,
        eyebrow: `${label} · shared record`,
        testId: T.preview,
        footer: /*#__PURE__*/ _jsxs("div", {
            className: "flex w-full items-center justify-end gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    "data-testid": T.previewClose,
                    onClick: onClose,
                    children: "Close"
                }),
                record && /*#__PURE__*/ _jsxs(Link, {
                    to: registryRecordHref(kind, id, origin),
                    className: specraButtonVariants({
                        variant: 'default'
                    }),
                    "data-testid": T.previewOpenRecord,
                    children: [
                        "Open ",
                        label
                    ]
                })
            ]
        }),
        children: /*#__PURE__*/ _jsx("div", {
            className: "space-y-4 p-4 text-sm",
            children: loading ? /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "Loading…"
            }) : !record ? /*#__PURE__*/ _jsx("output", {
                className: "block rounded-md border bg-muted px-3 py-2",
                "data-testid": T.previewUnavailable,
                children: "This record isn't available. It may have been removed, or you may not have access to it. Closing returns you to your work unchanged."
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("dl", {
                        className: "space-y-1",
                        children: [
                            company?.isOwnCompany && /*#__PURE__*/ _jsx("dd", {
                                className: "font-medium",
                                children: "Your organization"
                            }),
                            company?.cageCode && /*#__PURE__*/ _jsxs("dd", {
                                children: [
                                    "CAGE ",
                                    company.cageCode
                                ]
                            }),
                            company?.uei && /*#__PURE__*/ _jsxs("dd", {
                                children: [
                                    "UEI ",
                                    company.uei
                                ]
                            }),
                            vehicle?.contractNumber && /*#__PURE__*/ _jsxs("dd", {
                                children: [
                                    "Contract ",
                                    vehicle.contractNumber
                                ]
                            }),
                            vehicle?.agencyAcronym && /*#__PURE__*/ _jsxs("dd", {
                                children: [
                                    "Agency ",
                                    vehicle.agencyAcronym
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs font-medium uppercase text-muted-foreground",
                                children: "Connections"
                            }),
                            kind === 'company' ? holdings.isError ? /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Vehicle holdings could not be loaded."
                            }) : (holdings.data ?? []).length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "No Vehicle holdings recorded (not a verified zero)."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "list-disc pl-5",
                                children: (holdings.data ?? []).map((h)=>/*#__PURE__*/ _jsx("li", {
                                        children: h.vehicleName
                                    }, h.id))
                            }) : /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Holding Companies are listed from each Company's Vehicles section; open the Vehicle for its full record."
                            })
                        ]
                    })
                ]
            })
        })
    });
}
